import { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LogoMark from "@/components/LogoMark";
import { downloadImage, printElement } from "@/lib/pdf";
import { Printer, Image as ImageIcon } from "lucide-react";

const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const dayName = (iso) => {
  if (!iso) return "________";
  const d = new Date(iso.slice(0, 10));
  return isNaN(d.getTime()) ? "________" : DAYS[d.getDay()];
};
const minusOneDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10));
  if (isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export default function SlaughterNote({ order, open, onClose }) {
  if (!order) return null;
  const o = order;
  const bin = (o.child_gender || o.gender) === "perempuan" ? "BINTI" : "BIN";
  const sex = (o.lamb_sex || "").toLowerCase() === "betina" ? "BETINA" : "JANTAN";
  const eventDate = o.event_date || o.schedule_date;
  const slaughterDate = minusOneDay(eventDate);

  const Opt = ({ k, label }) => (
    <span className={`rounded-md px-2.5 py-1 text-base tracking-wide ${
      o.slaughter_option === k ? "bg-brand-espresso text-white" : "text-brand-espresso"
    }`}>
      {label}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-0">
        <DialogTitle className="sr-only">Berita Acara Penyembelihan {o.order_number}</DialogTitle>
        <div className="flex items-center justify-between border-b border-brand-line px-6 py-4 no-print">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Berita Acara Penyembelihan</h2>
          <div className="flex gap-2">
            <Button onClick={() => downloadImage("slaughter-print", `berita-acara-${o.order_number}.jpg`)} data-testid="jpg-slaughter-btn"
              variant="outline" className="gap-2 border-brand-line">
              <ImageIcon className="h-4 w-4" /> JPG
            </Button>
            <Button onClick={() => printElement("slaughter-print", { landscape: true })} data-testid="print-slaughter-btn"
              className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Printer className="h-4 w-4" /> Cetak
            </Button>
          </div>
        </div>

        <div id="slaughter-print" className="bg-white p-10 text-brand-espresso">
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-brand-gold pb-5">
            <div className="flex items-center gap-3">
              <LogoMark size={60} textClass="text-3xl" />
              <div className="leading-tight">
                <p className="font-serif text-2xl font-bold text-brand-espresso">Rizqia Aqiqah</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-brand-espresso">Hemat, Praktis, Amanah</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-brand-espresso">No. Pesanan</p>
              <p className="font-semibold">{o.order_number}</p>
            </div>
          </div>

          {/* Title */}
          <h1 className="mt-6 mb-6 text-center font-serif text-3xl font-bold tracking-wide">AQIQAH ATAS NAMA :</h1>

          {/* Atas nama */}
          <div className="rounded-xl border border-brand-line bg-brand-surface/50 p-5 text-center">
            <p className="font-serif text-5xl font-bold leading-tight">
              {(o.child_name || o.aqiqah_name || "-").toUpperCase()} <span className="text-brand-gold">{bin}</span><br />{(o.father_name || "-").toUpperCase()}
            </p>
          </div>

          {/* Jadwal */}
          <div className="mt-5 grid grid-cols-2 gap-4 text-center">
            <div className="rounded-lg border border-brand-line p-3">
              <p className="text-xs uppercase tracking-wide text-brand-espresso">Hari</p>
              <p className="mt-1 text-lg font-bold">{dayName(slaughterDate)}</p>
            </div>
            <div className="rounded-lg border border-brand-line p-3">
              <p className="text-xs uppercase tracking-wide text-brand-espresso">Tanggal</p>
              <p className="mt-1 text-lg font-bold">{formatDate(slaughterDate)}</p>
            </div>
          </div>

          {/* Tipe & jumlah */}
          <div className="mt-5 rounded-xl border-2 border-brand-espresso p-4 text-center">
            <p className="font-serif text-2xl font-bold tracking-wide">
              {(o.lamb_type || "TIPE -").toUpperCase()} — {sex} <span className="text-brand-espresso">(DIMASAK)</span>
            </p>
            <p className="mt-1 text-xl font-bold text-brand-gold">{o.lamb_count || 1} EKOR</p>
          </div>

          {/* Penyembelihan */}
          <div className="mt-5 text-center">
            <p className="mb-2 text-sm uppercase tracking-wide text-brand-espresso">Metode Penyembelihan</p>
            <div className="flex items-center justify-center gap-2">
              <Opt k="dokumentasi" label="DOKUMENTASI" />
              <span className="text-brand-muted">/</span>
              <Opt k="disaksikan" label="DISAKSIKAN" />
              <span className="text-brand-muted">/</span>
              <Opt k="video_call" label="VIDEO CALL" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
